import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Button } from 'react-bootstrap';
import AdminDataTable from './AdminDataTable';
import Swal from 'sweetalert2';
import { FaEdit, FaTrash, FaPlus, FaUserCog } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminPageHeader from './AdminPageHeader';

function DefaultKaryawanModules() {
    const [defaultModules, setDefaultModules] = useState([]);
    const [moduleMap, setModuleMap] = useState({});
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }

        const fetchData = async () => {
            await fetchModules(token);  // ✅ Ensure modules load first
        };

        fetchData();
    }, []);

    // Re-run fetchDefaultKaryawanModules only when moduleMap is updated
    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (Object.keys(moduleMap).length > 0) {
            fetchDefaultKaryawanModules(token);
        }
    }, [moduleMap]);

    /** ✅ Fetch Modules (ID -> Name Mapping) */
    const fetchModules = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}modulelist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });

            const data = await response.json();
            if (data.error === "0" && Array.isArray(data.results)) {
                const mapping = {};
                data.results.forEach(module => {
                    mapping[module.ID] = module.Modulename; // ✅ Store module ID -> Name mapping
                });

                setModuleMap(mapping);
            }
        } catch (err) {
            console.error('Failed to fetch modules:', err);
        }
    };

    /** ✅ Fetch Default Karyawan Modules */
    const fetchDefaultKaryawanModules = async (token) => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}default-karyawan-modules`, {
                method: 'GET',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
            });

            const data = await response.json();
            if (data.error === 0 && Array.isArray(data.data)) {
                const enrichedModules = data.data.map(role => ({
                    ...role,
                    modules_list: role.modules
                        .map(moduleId => moduleMap[moduleId] || `Unknown (ID: ${moduleId})`) // ✅ Show Name Instead of ID
                        .join(', ') || "No Modules"
                }));
                setDefaultModules(enrichedModules);
            }
        } catch (err) {
            console.error('Failed to fetch default karyawan modules:', err);
        } finally {
            setLoading(false);
        }
    };

    /** ✅ Handle Delete */
    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "This action cannot be undone!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                const token = localStorage.getItem('sessionToken');
                try {
                    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}default-karyawan-module`, {
                        method: 'DELETE',
                        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                        body: JSON.stringify({ id:id })
                    });

                    const data = await response.json();
                    console.log('Delete Response:', data);
                    if (data.error === 0) {
                        Swal.fire('Deleted!', 'Default module has been deleted.', 'success');
                        fetchDefaultKaryawanModules(token);
                    } else {
                        Swal.fire('Error!', 'Failed to delete default module.', 'error');
                    }
                } catch (err) {
                    console.error('Failed to delete default module:', err);
                    Swal.fire('Error!', 'Something went wrong.', 'error');
                }
            }
        });
    };

    /** ✅ Table Columns */
    const columns = [
        { name: 'ID', selector: row => row.id, sortable: true, width: '80px' },
        { name: 'Role Name', selector: row => row.role_name, sortable: true },
        { name: 'Modules', selector: row => row.modules_list, sortable: true },
        {
            name: 'Actions',
            cell: row => (
                <div className="d-flex gap-2">
                    <Button variant="outline-warning" size="sm" onClick={() => navigate(`/edit-default-karyawan-module/${row.id}`)}>
                        <FaEdit /> Edit
                    </Button>
                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(row.id)}>
                        <FaTrash /> Delete
                    </Button>
                </div>
            ),
            width: '200px'
        }
    ];

    return (
        <div>
            <Navigation />
            <Container fluid className="admin-page-shell mt-4 px-4">
                <AdminPageHeader
                    icon={FaUserCog}
                    eyebrow="Access policy"
                    title="Default Employee Access"
                    description="Tetapkan modul standar berdasarkan peran karyawan agar pemberian akses operasional berlangsung secara konsisten."
                    breadcrumbs={[
                        { label: 'Dashboard', to: '/dashboardadmin' },
                        { label: 'Default Employee Access' },
                    ]}
                    actions={(
                        <Button variant="primary" onClick={() => navigate('/add-default-karyawan-module')}>
                            <FaPlus /> Add Role Policy
                        </Button>
                    )}
                    meta={(
                        <span className="soft-stat">
                            {defaultModules.length} role policies
                        </span>
                    )}
                />
                <div className="section-card table-card">
                    <div className="table-toolbar">
                        <span className="status-chip">{defaultModules.length} role policies</span>
                    </div>
                    <div className="card-body pt-0">
                        <AdminDataTable
                          columns={columns}
                          data={defaultModules}
                          pagination
                          progressPending={loading}
                          emptyTitle="No default employee access policies found"
                          emptyDescription="Tambahkan kebijakan akses standar untuk peran karyawan."
                        />
                    </div>
                </div>
            </Container>
        </div>
    );
}

export default DefaultKaryawanModules;
